import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Building2, Mail, Phone, MapPin, Edit, Trash2, Upload, Image, X, Users, Eye } from 'lucide-react';
import { toast } from 'sonner';

export default function Companies() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', address: '', rccm: '', id_nat: '', nif: '', country: ''
  });

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des entreprises');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await api.put(`/companies/${selectedCompany.id}`, formData);
        toast.success('Entreprise modifiée avec succès');
      } else {
        await api.post('/companies', formData);
        toast.success('Entreprise créée avec succès');
      }
      setOpen(false);
      resetForm();
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    }
  };

  const handleEdit = (company) => {
    setEditMode(true);
    setSelectedCompany(company);
    setFormData({
      name: company.name,
      email: company.email,
      phone: company.phone || '',
      address: company.address || '',
      rccm: company.rccm || '',
      id_nat: company.id_nat || '',
      nif: company.nif || '',
      country: company.country
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette entreprise?')) {
      try {
        await api.delete(`/companies/${id}`);
        toast.success('Entreprise supprimée avec succès');
        fetchCompanies();
      } catch (error) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const handleLogoUpload = async (companyId, file) => {
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await api.post(`/companies/${companyId}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Logo uploadé avec succès');
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload du logo');
    } finally {
      setUploading(false);
    }
  };

  const handleLogoDelete = async (companyId) => {
    if (!window.confirm('Supprimer le logo de cette entreprise ?')) return;
    
    try {
      await api.delete(`/companies/${companyId}/logo`);
      toast.success('Logo supprimé');
      fetchCompanies();
    } catch (error) {
      toast.error('Erreur lors de la suppression du logo');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', address: '', rccm: '', id_nat: '', nif: '', country: '' });
    setEditMode(false);
    setSelectedCompany(null);
  };

  const getLogoUrl = (logoPath) => {
    if (!logoPath) return null;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Logo path already includes /api/uploads prefix from backend
    return `${backendUrl}${logoPath}`;
  };

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Entreprises</h1>
          <p className="text-slate-600 mt-1">Gestion des entreprises clientes</p>
        </div>
        {isSuperAdmin && (
          <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary-700" data-testid="add-company-button">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter Entreprise
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editMode ? 'Modifier' : 'Nouvelle'} Entreprise</DialogTitle>
                <DialogDescription>
                  {editMode ? 'Modifiez les informations' : 'Ajoutez une nouvelle entreprise'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom de l'entreprise *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="company-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="company-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="company-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Adresse</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    data-testid="company-address-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>RCCM</Label>
                    <Input value={formData.rccm} onChange={(e) => setFormData({...formData, rccm: e.target.value})} data-testid="company-rccm-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Id Nat</Label>
                    <Input value={formData.id_nat} onChange={(e) => setFormData({...formData, id_nat: e.target.value})} data-testid="company-idnat-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>NIF</Label>
                    <Input value={formData.nif} onChange={(e) => setFormData({...formData, nif: e.target.value})} data-testid="company-nif-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Pays *</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    required
                    data-testid="company-country-input"
                  />
                </div>
                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">
                    Annuler
                  </Button>
                  <Button type="submit" className="flex-1 bg-primary" data-testid="submit-company-button">
                    {editMode ? 'Modifier' : 'Créer'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Liste des Entreprises
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="font-semibold w-16">Logo</TableHead>
                  <TableHead className="font-semibold">Nom</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">Téléphone</TableHead>
                  <TableHead className="font-semibold">Pays</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                      Aucune entreprise trouvée
                    </TableCell>
                  </TableRow>
                ) : (
                  companies.map((company) => (
                    <TableRow key={company.id} className="hover:bg-slate-50">
                      <TableCell>
                        <div className="relative group">
                          {company.logo_url ? (
                            <div className="relative">
                              <img
                                src={getLogoUrl(company.logo_url)}
                                alt={`Logo ${company.name}`}
                                className="w-10 h-10 rounded-lg object-contain bg-white border"
                                data-testid={`company-logo-${company.id}`}
                              />
                              {(isSuperAdmin || user?.company_id === company.id) && (
                                <button
                                  onClick={() => handleLogoDelete(company.id)}
                                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Supprimer le logo"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-slate-400" />
                            </div>
                          )}
                          {(isSuperAdmin || user?.company_id === company.id) && (
                            <label 
                              className="absolute inset-0 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 rounded-lg flex items-center justify-center"
                              title="Changer le logo"
                            >
                              <Upload className="w-4 h-4 text-white" />
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                className="hidden"
                                onChange={(e) => handleLogoUpload(company.id, e.target.files[0])}
                                disabled={uploading}
                                data-testid={`upload-logo-${company.id}`}
                              />
                            </label>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{company.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-400" />
                          {company.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        {company.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-slate-400" />
                            {company.phone}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          {company.country}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          {isSuperAdmin && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-600 hover:bg-blue-50"
                              onClick={() => navigate(`/companies/${company.id}/employees`)}
                              title="Voir les employés de cette entreprise"
                              data-testid={`view-employees-${company.id}`}
                            >
                              <Users className="w-4 h-4 mr-1" />
                              Enquêter
                            </Button>
                          )}
                          {(isSuperAdmin || user?.company_id === company.id) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(company)}
                              data-testid={`edit-company-${company.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          {isSuperAdmin && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-error hover:bg-red-50"
                              onClick={() => handleDelete(company.id)}
                              data-testid={`delete-company-${company.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}